import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useState } from 'react'

export interface UserFormData {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  dateOfBirth: string
  gender: 'male' | 'female' | 'other' | ''
}
import { PhoneNumberInput } from '@/components/PhonenumberInput'
import { KeyIcon, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { CalenderOutlineIcon, LocationIcon } from '../Icons'
import { Calendar } from '../ui/calendar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { DEFAULT_USER, type User } from '@/schemas/user.schema'
import GooglePlaceAutoComplete, {
  type PlaceData,
} from '../GooglePlacesAutocomplete'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { validateEmail, validatePhoneNumber } from '@/utils/helper'
import { showErrorToast } from '../ui/toast'

interface UserDetailsFormProps {
  initialData?: Partial<User>
  onSubmit?: (data: User) => void
  onCancel?: () => void
  isLoading?: boolean
  isUploading?: boolean
  className?: string
  hasPhotoChanged?: boolean
}

export function UserDetailsForm({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  isUploading = false,
  className,
  hasPhotoChanged = false,
}: UserDetailsFormProps) {
  const initialForm = useMemo<User>(() => {
    return {
      ...DEFAULT_USER,
      ...(initialData as Partial<User>),
    }
  }, [initialData])
  const [formData, setFormData] = useState<User>(initialForm)
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth()
  )
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  )
  const [calendarDate, setCalendarDate] = useState<Date>(new Date())

  const [date, setDate] = useState<Date | undefined>(new Date())

  const [errors, setErrors] = useState<Partial<User>>({})
  const [phoneError, setPhoneError] = useState<string>('')
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [firstNameError, setFirstNameError] = useState('')
  const [lastNameError, setLastNameError] = useState('')
  const [emailError, setEmailError] = useState('')

  const handleInputChange = (
    field: keyof User | `address.${keyof User['address']}`,
    value: string
  ) => {
    if (typeof field === 'string' && field.startsWith('address.')) {
      const addrKey = field.split('.')[1] as keyof User['address']
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [addrKey]: value },
      }))
      if (errors.address) setErrors(prev => ({ ...prev, address: undefined }))
      return
    }

    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors as [typeof field])
      setErrors(prev => ({ ...prev, [field]: undefined }))
  }

  const isFormValid = useMemo(() => {
    const hasBasicInfo =
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.phoneNumber.trim() !== ''

    const noErrors = !firstNameError && !emailError && !phoneError

    return hasBasicInfo && noErrors
  }, [
    formData.firstName,
    formData.lastName,
    formData.phoneNumber,
    firstNameError,
    emailError,
    phoneError,
  ])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isFormValid) {
      showErrorToast(
        'Validation Error',
        'Please fill all required fields correctly'
      )
      return
    }
    onSubmit?.(formData)
  }

  const handleAddressChange = (value: string) => {
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        address: {
          ...DEFAULT_USER.address,
        },
      }))
      return
    }
    handleInputChange('address.street', value)
  }

  const handlePlaceSelect = (placeDetails: PlaceData) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        street: placeDetails.address ?? prev.address.street,
        city: placeDetails.city ?? prev.address.city,
        state: placeDetails.state ?? prev.address.state,
        country: placeDetails.country ?? prev.address.country,
        zipCode: placeDetails.zipCode ?? prev.address.zipCode,
      },
    }))
  }

  const handlePhoneChange = (value: string | undefined) => {
    const phoneValue = value ?? ''
    handleInputChange('phoneNumber', phoneValue)

    let error = ''
    if (!phoneValue.trim()) {
      error = 'Phone number is required'
    } else {
      error = validatePhoneNumber(phoneValue)
    }

    setPhoneError(error)
  }

  const handleFirstNameChange = (value: string) => {
    handleInputChange('firstName', value)
    if (!value.trim()) {
      setFirstNameError('First name is required')
    } else if (value.length >= 255) {
      setFirstNameError('First name cannot exceed 255 characters')
    } else {
      setFirstNameError('')
    }
  }

  const handleLastNameChange = (value: string) => {
    handleInputChange('lastName', value)
    // Validate immediately
    if (!value.trim()) {
      setLastNameError('Last name is required')
    } else if (value.length >= 255) {
      setLastNameError('Last name cannot exceed 255 characters')
    } else {
      setLastNameError('')
    }
  }

  const handleEmailChange = (value: string) => {
    handleInputChange('email', value)

    const error = validateEmail(value)
    setEmailError(error)
  }

  const clearAllErrors = () => {
    setFirstNameError('')
    setLastNameError('')
    setEmailError('')
    setPhoneError('')
  }

  const handleCancel = () => {
    setFormData(initialForm)
    clearAllErrors()
    onCancel?.()
  }

  const hasFormChanged = useMemo(() => {
    const isBasicFieldChanged =
      formData.firstName !== initialForm.firstName ||
      formData.lastName !== initialForm.lastName ||
      formData.phoneNumber !== initialForm.phoneNumber ||
      formData.email !== initialForm.email ||
      formData.birthDate !== initialForm.birthDate ||
      formData.gender !== initialForm.gender ||
      formData.username !== initialForm.username

    const isAddressChanged =
      formData.address?.street !== initialForm.address?.street ||
      formData.address?.city !== initialForm.address?.city ||
      formData.address?.state !== initialForm.address?.state ||
      formData.address?.country !== initialForm.address?.country ||
      formData.address?.zipCode !== initialForm.address?.zipCode

    return isBasicFieldChanged || isAddressChanged || hasPhotoChanged
  }, [formData, initialForm, hasPhotoChanged])

  const isFormValidForButton = useMemo(() => {
    return (
      formData.firstName.trim() !== '' ||
      formData.lastName.trim() !== '' ||
      formData.phoneNumber.trim() !== ''
    )
  }, [formData.firstName, formData.lastName, formData.phoneNumber])

  // Use formData.dateOfBirth directly from local state
  const selectedDate = formData.birthDate
    ? new Date(formData.birthDate)
    : undefined
  return (
    <div
      className={cn(
        'bg-white rounded-none lg:rounded-xl border border-transparent lg:border-ss-neutral-100 mx-0 lg:mx-6 flex-1 flex flex-col mb-6',
        className
      )}
    >
      <form onSubmit={handleSubmit} className="h-full flex flex-col flex-1">
        <div className="flex-1 space-y-6 p-3 lg:p-6 pb-6 max-w-2xl w-full lg:max-w-full mx-auto">
          {/* First Name & Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-1">
              <div className="flex items-center">
                <Label
                  variant="required"
                  htmlFor="firstName"
                  className="text-sm font-semibold text-ss-neutral-950"
                >
                  First Name
                </Label>
              </div>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={e => handleFirstNameChange(e.target.value)}
                placeholder="Enter First Name"
                className={cn(
                  'h-11 border-ss-neutral-100 bg-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20 text-base',
                  errors.firstName &&
                    'border-ss-critical-200 focus:border-ss-critical-200'
                )}
                aria-invalid={!!errors.firstName}
              />
              {firstNameError && (
                <p className="text-sm text-ss-critical-200">{firstNameError}</p>
              )}
            </div>

            <div className="space-y-1">
              <div className="flex items-center">
                <Label
                  htmlFor="lastName"
                  className="text-sm font-semibold text-ss-neutral-950"
                >
                  Last Name
                </Label>
                <span className="text-ss-critical-200 text-xs font-medium ml-1">
                  *
                </span>
              </div>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={e => handleLastNameChange(e.target.value)}
                placeholder="Enter Last Name"
                className={cn(
                  'h-11 border-ss-neutral-100 bg-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20 text-base',
                  errors.lastName &&
                    'border-ss-critical-200 focus:border-ss-critical-200'
                )}
                // aria-invalid={!!errors.lastName}
              />
              {lastNameError && (
                <p className="text-sm text-ss-critical-200">{lastNameError}</p>
              )}
            </div>
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-1">
              <Label
                htmlFor="email"
                className="text-sm font-medium text-ss-neutral-950"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={e => handleEmailChange(e.target.value)}
                placeholder="Enter Email"
                disabled
                className={cn(
                  'h-11  text-ss-neutral-300 text-base cursor-not-allowed',
                  errors.email && 'border-ss-critical-200'
                )}
                aria-invalid={!!errors.email}
              />
              {emailError && (
                <p className="text-sm text-ss-critical-200">{lastNameError}</p>
              )}
            </div>

            <div>
              <Label
                htmlFor="phone"
                variant="required"
                className="text-sm font-medium text-ss-neutral-950 mb-1"
              >
                Phone Number
              </Label>
              <PhoneNumberInput
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                placeholder="Enter Phone Number"
                className="w-full"
                error={phoneError}
                required
              />
            </div>
          </div>

          {/* Date of Birth & Gender */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="relative">
              <Label
                htmlFor="dateOfBirth"
                className="text-sm font-medium text-ss-neutral-950 mb-2.5"
              >
                Select Date of Birth
              </Label>

              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    id="registration-start-date-button"
                    className="w-full justify-start text-left font-normal border-ss-neutral-200 bg-ss-neutral-white hover:border-ss-blue-700 focus:border-ss-blue-700 pr-8 h-11"
                    aria-label="Select Date of Birth"
                  >
                    <span className="flex-1 truncate text-ellipsis overflow-hidden">
                      {selectedDate && !Number.isNaN(selectedDate.getTime()) ? (
                        <span className="text-ss-neutral-950">
                          {selectedDate.toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-ss-neutral-400">
                          Select birth date
                        </span>
                      )}
                    </span>
                    {!selectedDate && (
                      <CalenderOutlineIcon
                        className="h-4 w-4 text-ss-neutral-600"
                        aria-hidden="true"
                      />
                    )}
                  </Button>
                </PopoverTrigger>

                {selectedDate && (
                  <X
                    className="absolute top-11 right-2 h-4 w-4 text-ss-neutral-400 hover:text-ss-neutral-600 cursor-pointer z-10"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleInputChange('birthDate', '')
                    }}
                    aria-label="Clear start date"
                  />
                )}
                <PopoverContent className="w-auto p-4" align="start">
                  <div className="space-y-4">
                    {/* Month and Year Dropdowns */}
                    <div className="flex gap-2">
                      <Select
                        value={selectedMonth.toString()}
                        onValueChange={value => {
                          const month = Number.parseInt(value)
                          setSelectedMonth(month)
                          const newDate = new Date(selectedYear, month, 1)
                          setCalendarDate(newDate)
                        }}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem key={i} value={i.toString()}>
                              {new Date(2000, i, 1).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'long',
                                }
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        value={selectedYear.toString()}
                        onValueChange={value => {
                          const year = Number.parseInt(value)
                          setSelectedYear(year)
                          const newDate = new Date(year, selectedMonth, 1)
                          setCalendarDate(newDate)
                        }}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from(
                            { length: new Date().getFullYear() - 1940 + 1 },
                            (_, i) => {
                              const year = new Date().getFullYear() - i
                              return (
                                <SelectItem key={year} value={year.toString()}>
                                  {year}
                                </SelectItem>
                              )
                            }
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Calendar */}
                    <Calendar
                      className="w-full"
                      mode="single"
                      selected={date}
                      onSelect={d => {
                        setDate(d)
                        setPopoverOpen(false)
                        const iso = d ? d.toISOString() : ''
                        handleInputChange('birthDate' as keyof User, iso)
                      }}
                      aria-label="Calendar for date selection"
                      disabled={{ after: new Date() }}
                      month={calendarDate}
                      onMonthChange={setCalendarDate}
                    />
                  </div>
                </PopoverContent>
              </Popover>

              <p className="text-sm leading-4.5  font-medium text-ss-critical-200 mt-1" />
            </div>

            <div className="space-y-2.5 min-w-[290px]">
              <Label className="text-sm font-medium text-ss-neutral-950">
                Gender
              </Label>
              <RadioGroup
                value={formData.gender}
                onValueChange={(value: 'male' | 'female' | 'other') =>
                  handleInputChange('gender', value)
                }
                className="flex gap-4"
                aria-labelledby="gender-label"
              >
                {[
                  { id: 'male', label: 'Male' },
                  { id: 'female', label: 'Female' },
                  { id: 'other', label: 'Other' },
                ].map(option => (
                  <div key={option.id} className="relative mb-0 gap-3">
                    <Label
                      htmlFor={option.id}
                      className={cn(
                        'flex items-center gap-3 rounded-lg cursor-pointer transition-all duration-200 ',
                        formData.gender === option.id
                          ? 'border-ss-blue-500 bg-white'
                          : 'border-border bg-background'
                      )}
                    >
                      <RadioGroupItem
                        value={option.id}
                        id={option.id}
                        className={cn(
                          formData.gender === option.id
                            ? 'border-ss-blue-500 bg-ss-blue-500 text-white'
                            : 'border-border bg-background',
                          'size-5'
                        )}
                        aria-label={option.label}
                      />
                      <div className="flex items-start gap-3 flex-1">
                        <div className="flex-1">
                          <div className="font-normal text-ss-neutral-950 text-sm leading-tight">
                            {option.label}
                          </div>
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        </div>

        <Card className="border-none rounded-none bg-ss-neutral-white gap-0 pt-0 mb-0">
          <CardHeader className="pb-4 border-b border-t border-black/6 bg-ss-neutral-25 flex items-center gap-2 px-4 py-3 [.border-b]:pb-3 ">
            <KeyIcon
              className="h-5 w-5 text-ss-neutral-600"
              aria-hidden="true"
            />
            <CardTitle
              className="text-lg font-semibold text-ss-neutral-950"
              id="account-credentials-heading"
            >
              Account Credentials
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 mb-2">
                <Label
                  variant={'required'}
                  htmlFor="username"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter username"
                  disabled={true}
                  value={formData.username}
                  onChange={e => handleInputChange('username', e.target.value)}
                  className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                  required
                  aria-required="true"
                  aria-label="Username"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none rounded-none bg-ss-neutral-white gap-0 pt-0 mb-0">
          <CardHeader className="pb-4 border-b border-t border-black/6 bg-ss-neutral-25 flex items-center gap-2 px-4 py-3 [.border-b]:pb-3 ">
            <LocationIcon
              className="h-5 w-5 text-ss-neutral-600"
              aria-hidden="true"
            />
            <CardTitle
              className="text-lg font-semibold text-ss-neutral-950"
              id="address-info-heading"
            >
              Address Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {/* Country and State Selection */}
            <div className="space-y-2">
              <Label
                htmlFor="streetAddress"
                className="text-sm font-medium text-ss-neutral-950"
              >
                Street Address
              </Label>
              <GooglePlaceAutoComplete
                value={formData.address?.street ?? ''}
                handleChange={e => handleAddressChange(e.target.value)}
                handlePlaceSelect={handlePlaceSelect}
                placeholder="Enter complete street address"
                className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Country Dropdown with Search */}
              <div className="space-y-2">
                <Label
                  htmlFor="zipCode"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  ZIP Code
                </Label>
                <Input
                  id="zipCode"
                  type="text"
                  placeholder="Enter ZIP"
                  value={formData.address?.zipCode ?? ''}
                  disabled
                  className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                  aria-label="ZIP Code"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="country"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  Country
                </Label>
                <Input
                  id="country"
                  type="text"
                  placeholder="Enter country"
                  value={formData.address?.country ?? ''}
                  disabled
                  className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                />
              </div>
            </div>

            {/* City  */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* State Dropdown with Search */}
              <div className="space-y-2">
                <Label
                  htmlFor="state"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  State / Province
                </Label>
                <Input
                  id="state"
                  type="text"
                  placeholder="Enter state"
                  value={formData.address?.state ?? ''}
                  disabled
                  className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                />
              </div>
              {/* City Dropdown */}
              <div className="space-y-2">
                <Label
                  htmlFor="city"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  City
                </Label>
                <Input
                  id="city"
                  type="text"
                  placeholder="Enter city"
                  value={formData.address?.city ?? ''}
                  disabled
                  className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                />
              </div>
            </div>
            {/* Street Address - Full Width */}
          </CardContent>
        </Card>

        {/* Footer with buttons */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-ss-action-gray-100 rounded-bl-xl rounded-br-xl inset-shadow-md">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading || !hasFormChanged}
            className="h-9 px-4 border-ss-neutral-200 text-ss-neutral-200 hover:bg-ss-neutral-50"
          >
            Cancel
          </Button>

          <Button
            type="submit"
            // size="sm"
            disabled={
              isLoading ||
              !isFormValidForButton ||
              isUploading ||
              !hasFormChanged
            }
            // className="h-9 px-4 bg-ss-neutral-200 hover:bg-ss-neutral-300 text-ss-neutral-25"
          >
            {isLoading && (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Saving User...
              </>
            )}
            {!isLoading && isUploading && (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Uploading Image...
              </>
            )}
            {!isLoading && !isUploading && 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}
